(() => {
  const motionOk = () => window.matchMedia("(prefers-reduced-motion: no-preference)").matches;

  const bindFilmInset = () => {
    const sections = document.querySelectorAll(".immo-film .shopify-section--video, [data-immo-film]");
    if (!sections.length) return;

    const update = () => {
      const reduce = !motionOk() || window.matchMedia("(max-width: 799px)").matches;
      sections.forEach((section) => {
        if (reduce) {
          section.style.setProperty("--film-inset", "0");
          return;
        }
        const top = section.getBoundingClientRect().top;
        const extra = section.offsetHeight - window.innerHeight;
        const run = extra > 80 ? extra : Math.max(section.offsetHeight * 0.55, 1);
        const progress = Math.min(1, Math.max(0, -top / run));
        section.style.setProperty("--film-inset", progress.toFixed(3));
      });
    };

    let ticking = false;
    window.addEventListener(
      "scroll",
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          update();
          ticking = false;
        });
      },
      { passive: true }
    );
    update();
  };

  const bindStatsHairline = () => {
    const section = document.querySelector(".shopify-section--stats");
    if (!section || !("IntersectionObserver" in window)) return;
    if (!motionOk()) {
      section.classList.add("is-drawn");
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        section.classList.add("is-drawn");
        observer.disconnect();
      },
      { threshold: 0.4 }
    );
    observer.observe(section);
  };

  const bindMap = () => {
    document.querySelectorAll("[data-immo-map]").forEach((root) => {
      const path = root.querySelector("[data-map-path]");
      if (!path || !("IntersectionObserver" in window)) return;
      const length = path.getTotalLength();
      path.style.strokeDasharray = `${length}`;
      path.style.strokeDashoffset = motionOk() ? `${length}` : "0";
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          path.style.strokeDashoffset = "0";
          observer.disconnect();
        },
        { threshold: 0.35 }
      );
      observer.observe(root);
    });
  };

  const bindPortfolio = () => {
    document.querySelectorAll("[data-immo-portfolio]").forEach((root) => {
      const buttons = root.querySelectorAll("[data-filter]");
      const cards = root.querySelectorAll("[data-status]");
      buttons.forEach((button) => {
        button.addEventListener("click", () => {
          const filter = button.getAttribute("data-filter");
          buttons.forEach((other) => other.setAttribute("aria-pressed", String(other === button)));
          cards.forEach((card) => {
            const show = filter === "all" || card.getAttribute("data-status") === filter;
            card.hidden = !show;
          });
        });
      });
    });
  };

  const bindPressRail = () => {
    document.querySelectorAll("[data-immo-press-rail]").forEach((root) => {
      const track = root.querySelector(".press-rail__scroller");
      if (!track) return;
      const originals = [...track.children];
      if (originals.length < 5) return;

      originals.forEach((card) => track.appendChild(card.cloneNode(true)));

      let index = 0;
      let timer = 0;
      let hovering = false;
      let wrapping = false;
      const gap = () => {
        const styles = window.getComputedStyle(track);
        return Number.parseFloat(styles.columnGap || styles.gap) || 20;
      };
      const stepWidth = () => originals[0].getBoundingClientRect().width + gap();
      const apply = (withMotion) => {
        track.style.transition = withMotion && motionOk() ? "transform 0.65s cubic-bezier(0.16, 1, 0.3, 1)" : "none";
        track.style.transform = `translate3d(${-index * stepWidth()}px, 0, 0)`;
      };
      const go = (delta) => {
        if (wrapping) return;
        if (delta > 0) {
          if (index >= originals.length) return;
          index += 1;
          apply(true);
          return;
        }
        if (index <= 0) {
          wrapping = true;
          index = originals.length;
          apply(false);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              index = originals.length - 1;
              wrapping = false;
              apply(true);
            });
          });
          return;
        }
        index -= 1;
        apply(true);
      };
      const advance = () => {
        if (hovering || document.hidden) return;
        go(1);
      };
      const start = () => {
        window.clearInterval(timer);
        if (!motionOk() || hovering) return;
        timer = window.setInterval(advance, 4200);
      };

      track.addEventListener("transitionend", (event) => {
        if (event.target !== track || event.propertyName !== "transform") return;
        if (index < originals.length) return;
        index = 0;
        apply(false);
      });
      root.querySelector("[data-prev]")?.addEventListener("click", (event) => {
        event.preventDefault();
        go(-1);
      });
      root.querySelector("[data-next]")?.addEventListener("click", (event) => {
        event.preventDefault();
        go(1);
      });
      root.addEventListener("mouseenter", () => {
        hovering = true;
        window.clearInterval(timer);
      });
      root.addEventListener("mouseleave", () => {
        hovering = false;
        start();
      });
      root.addEventListener("focusin", () => {
        hovering = true;
        window.clearInterval(timer);
      });
      root.addEventListener("focusout", (event) => {
        if (root.contains(event.relatedTarget)) return;
        hovering = false;
        start();
      });
      window.addEventListener("resize", () => apply(false), { passive: true });
      apply(false);
      start();
    });
  };

  const bindGallery = () => {
    document.querySelectorAll("[data-immo-gallery]").forEach((root) => {
      const slides = [...root.querySelectorAll("[data-slide]")];
      if (slides.length < 2) return;
      let index = 0;
      const show = (next) => {
        slides[index].classList.remove("is-active");
        index = (next + slides.length) % slides.length;
        slides[index].classList.add("is-active");
      };
      root.querySelector("[data-prev]")?.addEventListener("click", () => show(index - 1));
      root.querySelector("[data-next]")?.addEventListener("click", () => show(index + 1));
      if (!motionOk()) return;
      let timer = setInterval(() => show(index + 1), 4200);
      root.addEventListener("mouseenter", () => clearInterval(timer));
      root.addEventListener("mouseleave", () => {
        timer = setInterval(() => show(index + 1), 4200);
      });
    });
  };

  const ARROW_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>';

  const enhanceButton = (button) => {
    if (!(button instanceof HTMLElement) || button.classList.contains("button--enhanced")) return;
    button.classList.add("button--enhanced");
    if (button.querySelector(".button__glow")) return;

    const existing = button.innerHTML.trim();
    button.innerHTML = `
      <span class="button__glow" aria-hidden="true"></span>
      <span class="button__sheen" aria-hidden="true"></span>
      <span class="button__label">${existing}<span class="button__underline" aria-hidden="true"></span></span>
      <span class="button__arrow" aria-hidden="true">${ARROW_SVG}</span>
    `;
  };

  const bindButtons = () => {
    document.querySelectorAll(".button").forEach(enhanceButton);

    const finePointer = window.matchMedia("(pointer: fine)");
    document.addEventListener(
      "pointermove",
      (event) => {
        if (!finePointer.matches) return;
        const button = event.target instanceof Element ? event.target.closest(".button") : null;
        if (!button || button.hasAttribute("disabled") || button.getAttribute("aria-disabled") === "true") return;
        const rect = button.getBoundingClientRect();
        button.style.setProperty("--glow-x", `${event.clientX - rect.left}px`);
        button.style.setProperty("--glow-y", `${event.clientY - rect.top}px`);
      },
      { passive: true }
    );
  };

  const bindCardVideos = () => {
    document.querySelectorAll("[data-immo-card-video]").forEach((card) => {
      const video = card.querySelector("video");
      if (!(video instanceof HTMLVideoElement)) return;
      video.muted = true;
      video.playsInline = true;

      const morph = () => {
        if (card.classList.contains("is-playing")) return;
        const reveal = () => card.classList.add("is-playing");
        const delay = motionOk() ? 480 : 0;
        window.setTimeout(() => {
          const play = video.play();
          if (play && typeof play.then === "function") {
            play.then(reveal).catch(reveal);
          } else {
            reveal();
          }
        }, delay);
      };

      const startWhenReady = () => {
        if (video.readyState >= 2) {
          morph();
          return;
        }
        video.addEventListener("canplay", morph, { once: true });
      };

      if (!motionOk()) return;
      if (!("IntersectionObserver" in window)) {
        startWhenReady();
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          startWhenReady();
          observer.disconnect();
        },
        { threshold: 0.4 }
      );
      observer.observe(card);
    });
  };

  bindFilmInset();
  bindStatsHairline();
  bindMap();
  bindPortfolio();
  bindGallery();
  bindPressRail();
  bindButtons();
  bindCardVideos();
})();
